// Config panel for the "route_call" action block
// Supports single, ring group (simultaneous), and sequential (failover) routing
import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const MODES = [
  { value: 'single', label: 'Single Extension', description: 'Ring one extension' },
  { value: 'group', label: 'Ring Group (all at once)', description: 'Ring multiple extensions simultaneously — first to answer gets the call' },
  { value: 'sequential', label: 'Sequential (failover)', description: 'Try extensions in order — if not answered, try the next one' },
];

const RouteCallConfig = ({ config, onChange, onSummaryChange }) => {
  const mode = config.mode || 'single';
  const targets = config.targets || [{ extension: '', timeout: 30 }];

  const updateConfig = (updates) => {
    const updated = { ...config, ...updates };
    onChange(updated);

    // Build summary
    const m = updated.mode || 'single';
    const t = updated.targets || [{ extension: '' }];
    const exts = t.map(x => x.extension || '?').filter(Boolean);
    const dtmf = updated.dtmf ? ` +DTMF` : '';

    if (m === 'group') {
      onSummaryChange(`Ring all: ${exts.join(', ')}${dtmf}`);
    } else if (m === 'sequential') {
      onSummaryChange(`Try: ${exts.join(' → ')}${dtmf}`);
    } else {
      onSummaryChange(`Dial ${exts[0] || '?'} (${t[0]?.timeout || 30}s)${dtmf}`);
    }
  };

  const handleModeChange = (newMode) => {
    // When switching to single, keep only first target
    let newTargets = [...targets];
    if (newMode === 'single' && newTargets.length > 1) {
      newTargets = [newTargets[0]];
    }
    // When switching to group/sequential, ensure at least 2 targets
    if ((newMode === 'group' || newMode === 'sequential') && newTargets.length < 2) {
      newTargets.push({ extension: '', timeout: 30 });
    }
    updateConfig({ mode: newMode, targets: newTargets });
  };

  const handleTargetChange = (index, field, value) => {
    const newTargets = targets.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    );
    updateConfig({ targets: newTargets });
  };

  const addTarget = () => {
    updateConfig({ targets: [...targets, { extension: '', timeout: 30 }] });
  };

  const removeTarget = (index) => {
    if (targets.length <= 1) return;
    updateConfig({ targets: targets.filter((_, i) => i !== index) });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Route the incoming call to one or more extensions.
      </Typography>

      <TextField
        size="small"
        select
        label="Routing Mode"
        value={mode}
        onChange={(e) => handleModeChange(e.target.value)}
      >
        {MODES.map((m) => (
          <MenuItem key={m.value} value={m.value}>
            <Box>
              <Typography variant="body2">{m.label}</Typography>
              <Typography variant="caption" color="text.secondary">{m.description}</Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <Divider />

      <Typography variant="caption" fontWeight={600}>
        {mode === 'sequential' ? 'Extensions (tried in order)' :
         mode === 'group' ? 'Extensions (ring simultaneously)' :
         'Target Extension'}
      </Typography>

      {targets.map((target, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          {mode === 'sequential' && (
            <Typography variant="caption" sx={{ mt: 1.5, minWidth: 20, color: 'text.secondary', fontWeight: 700 }}>
              {index + 1}.
            </Typography>
          )}
          <TextField
            size="small"
            label={mode === 'single' ? 'Extension' : `Extension ${index + 1}`}
            value={target.extension || ''}
            onChange={(e) => handleTargetChange(index, 'extension', e.target.value)}
            required
            placeholder="e.g. 6001"
            sx={{ flex: 1 }}
            helperText={index === 0 ? 'Supports variables: {{callee}}, {{caller}}' : undefined}
          />
          <TextField
            size="small"
            label="Timeout"
            type="number"
            value={target.timeout ?? 30}
            onChange={(e) => handleTargetChange(index, 'timeout', Math.min(120, Math.max(5, parseInt(e.target.value) || 30)))}
            inputProps={{ min: 5, max: 120 }}
            sx={{ width: 90 }}
            helperText="sec"
          />
          {mode !== 'single' && targets.length > 1 && (
            <IconButton size="small" onClick={() => removeTarget(index)} sx={{ mt: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}

      {mode !== 'single' && targets.length < 5 && (
        <Box>
          <IconButton size="small" onClick={addTarget} color="primary">
            <AddIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary">Add extension</Typography>
        </Box>
      )}

      <Divider />

      <TextField
        size="small"
        label="DTMF after answer (optional)"
        value={config.dtmf || ''}
        onChange={(e) => updateConfig({ dtmf: e.target.value.replace(/[^0-9*#A-Da-d]/g, '') })}
        placeholder="e.g. 1234"
        helperText="Send these DTMF digits to the answering device after pickup (e.g. for door openers)"
      />

      {mode === 'sequential' && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
          Each extension is tried in order. If not answered within its timeout,
          the next extension rings. After all targets are exhausted, the call
          goes to the last extension's voicemail.
        </Alert>
      )}

      {mode === 'group' && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
          All extensions ring simultaneously. The first one to answer gets the call.
          The timeout applies to the whole group.
        </Alert>
      )}

      {config.dtmf && (
        <Alert severity="warning" variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
          DTMF <code>{config.dtmf}</code> will be sent automatically after the call is answered.
          Common use: door controllers that require a code to unlock.
        </Alert>
      )}
    </Box>
  );
};

export default RouteCallConfig;
